import { TError } from "../../types/error";

import { EstimateAdapter } from "./adapter/estimate";
import { ParentRangesAdapter } from "./adapter/parent-ranges";
import { EstimationRecordsAdapter } from "./adapter/estimation-records";
import { SmdBalanceAdapter } from "./adapter/smd-balance";
import { ISimulationRepo } from "./irepo";
import {
  EstimationDef,
  EstimationRecord,
  EstimationTaskDef,
  InputUploadDef,
  SmdBalance,
} from "./type/batch-def";
import {
  OutcomeParentRanges,
  ParentRangesByIdCode,
  SimulationEstimate,
} from "./type/simulation-def";

export class SimulationUsecase {
  private repo: ISimulationRepo;
  private error: (error: TError) => void;

  constructor(repo: ISimulationRepo, error: (error: TError) => void) {
    this.repo = repo;
    this.error = error;
  }

  /** アウトカムの親変数とレンジ。スライダーを出せないものは除いて返す。 */
  async fetchOutcomeParentRanges(
    asset_id: string,
    outcome: string
  ): Promise<OutcomeParentRanges> {
    try {
      const data = await this.repo.fetchOutcomeParentRanges({ asset_id, outcome });
      return ParentRangesAdapter.toOutputDTO(data);
    } catch (error) {
      this.error({ id: "simulation", error });
      throw error;
    }
  }

  /**
   * 識別コードごとの実測値つきで親変数のレンジを取得する。
   *
   * 画面では識別コードのドロップダウンに使い、選ばれたコードの実測値を
   * スライダーへ流し込む。
   */
  async fetchParentRangesByIdCode(
    asset_id: string,
    outcome: string
  ): Promise<ParentRangesByIdCode> {
    try {
      const data = await this.repo.fetchOutcomeParentRangesByIdCode({
        asset_id,
        outcome,
      });
      return ParentRangesAdapter.toByIdCodeDTO(data);
    } catch (error) {
      this.error({ id: "simulation", error });
      throw error;
    }
  }

  /**
   * 推定を実行する。`mq/` 配下だが**同期 API** で、結果がそのまま返る。
   *
   * @param modelId dwmodel(asset) の id。GCM ではない
   *
   * `gcm_id` は任意だが**渡さない**。旧 GCM を再利用すると分布・前処理がずれるため
   * BE から当面は未指定でという指示が出ている（2026-10-27）。
   */
  async estimate(
    modelId: string,
    interventions: Record<string, number>,
    samplesPerProfile: number
  ): Promise<SimulationEstimate | null> {
    try {
      const res = await this.repo.postEstimate(modelId, {
        interventions,
        method_config: { samples_per_profile: samplesPerProfile },
      });
      return EstimateAdapter.toOutputDTO(res);
    } catch (error) {
      this.error({ id: "simulation", error });
      throw error;
    }
  }

  // --- ② バッチアウトカムシミュレーション -------------------------------

  /** 介入 CSV のテンプレートをダウンロードさせる。 */
  async downloadInputTemplate(modelId: string, fileName: string): Promise<void> {
    try {
      const blob = await this.repo.fetchInputTemplate(modelId);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = fileName;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      this.error({ id: "simulation", error });
      throw error;
    }
  }

  /**
   * 介入 CSV をアップロードする。
   *
   * 署名付き URL の発行 → その URL へ直接 PUT の 2 段。PUT 側には
   * `Authorization` を付けない（URL 自体が権限を持っているため）。
   */
  async uploadInterventionCsv(
    modelId: string,
    file: File
  ): Promise<InputUploadDef> {
    try {
      const upload = await this.repo.createInputUpload({
        model_id: modelId,
        file_name: file.name,
      });
      await this.repo.uploadToSignedUrl(
        upload.upload_url,
        file,
        upload.required_headers ?? {}
      );
      return upload;
    } catch (error) {
      this.error({ id: "simulation", error });
      throw error;
    }
  }

  /**
   * アップロードしたデータと元データの共変量バランス（SMD）を取得する。
   *
   * `ipwRunId` を渡すと選択バイアス調整後の SMD になる。
   */
  async fetchSmdBalance(
    asset_id: string,
    upload_id: string,
    ipwRunId?: string
  ): Promise<SmdBalance> {
    try {
      const data = await this.repo.fetchSmdBalance({
        asset_id,
        upload_id,
        ...(ipwRunId ? { ipw_run_id: ipwRunId } : {}),
      });
      return SmdBalanceAdapter.toOutputDTO(data);
    } catch (error) {
      this.error({ id: "simulation", error });
      throw error;
    }
  }

  /**
   * 選択バイアス調整（IPW）を計算する。
   *
   * 返る `ipw_run_id` を `fetchSmdBalance` に渡すと、調整後の SMD を引ける。
   */
  async computeIpw(asset_id: string, upload_id: string): Promise<string> {
    try {
      const res = await this.repo.computeIpw({ asset_id, upload_id });
      return res.ipw_run_id;
    } catch (error) {
      this.error({ id: "simulation", error });
      throw error;
    }
  }

  /**
   * バッチ推定を開始する。① と違い非同期なので、ここでは待たずに task_id を返す。
   *
   * `gcm_id` は送らない（旧 GCM の再利用は分布・前処理がずれるため BE が非推奨）。
   */
  async startEstimationTask(
    modelId: string,
    inputFile: { storage_type: string; saved_path: string },
    samplesPerProfile: number,
    ipwRunId?: string
  ): Promise<string> {
    try {
      const task = await this.repo.createEstimationTask({
        model_id: modelId,
        input_file: inputFile,
        method_config: { samples_per_profile: samplesPerProfile },
        // 選択バイアス調整を反映する場合のみ渡す
        ...(ipwRunId ? { ipw_run_id: ipwRunId } : {}),
      });
      return task.task_id;
    } catch (error) {
      this.error({ id: "simulation", error });
      throw error;
    }
  }

  /**
   * 推定タスクの状態を 1 回だけ引く。ポーリングは画面側の `refetchInterval` で回す
   * （ルール 26）。完了していなければ `estimation_id` は null のまま返る。
   */
  async fetchEstimationTask(taskId: string): Promise<EstimationTaskDef> {
    try {
      return await this.repo.fetchEstimationTask(taskId);
    } catch (error) {
      this.error({ id: "simulation", error });
      throw error;
    }
  }

  /** 推定結果のメタデータと行をまとめて取る。 */
  async fetchEstimationResult(estimationId: string): Promise<{
    estimation: EstimationDef;
    records: EstimationRecord[];
  }> {
    try {
      const [estimation, records] = await Promise.all([
        this.repo.fetchEstimation(estimationId),
        this.repo.fetchEstimationRecords(estimationId),
      ]);
      return {
        estimation,
        records: EstimationRecordsAdapter.toOutputDTO(records),
      };
    } catch (error) {
      this.error({ id: "simulation", error });
      throw error;
    }
  }

  /** 推定結果の CSV をダウンロードさせる。 */
  async downloadEstimation(
    estimationId: string,
    fileName: string
  ): Promise<void> {
    try {
      const blob = await this.repo.fetchEstimationDownload(estimationId);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = fileName;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      this.error({ id: "simulation", error });
      throw error;
    }
  }

  /** スライダーの初期値（レンジの中央）。 */
  initialValues(def: OutcomeParentRanges): Record<string, number> {
    return ParentRangesAdapter.initialValues(def);
  }
}
